'use client';
import {
  privateQueryContext,
  privateQueryOptions,
} from '@eliadmin/core/src/utils/private-query-context';

import { useQuery } from '@tanstack/react-query';

import { fetchOrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCustomerOrders } from '@eliadmin/core/src/api/customers/customers-api';
import { FetchError } from '@eliadmin/core/src/api/fetcher';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

/**
 * Orders per request; the loading still follows `meta.last_page` when the API pages differently.
 */
const PAGE_SIZE = 50;

function timestamp(value: string) {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * All orders of the signed-in customer, newest first. The API pages them, but the filter counts and
 * the search need the whole list, so every page is loaded.
 */
export function useCustomerOrders() {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken ?? '');
  const customerId = useCustomerStore(s => s.me?.customer.id);

  return useQuery({
    queryKey: [CacheKey.ORDERS, ...privateQueryContext(locale, locale, customerId, 'all')],
    ...privateQueryOptions,
    enabled: !!authToken,
    queryFn: async () => {
      const page = (number: number) =>
        fetchCustomerOrders({ locale, authToken, query: { page: number, results: PAGE_SIZE } });
      const first = await page(1);
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, first.meta.last_page - 1) }, (_, index) =>
          page(index + 2),
        ),
      );
      return [first, ...rest]
        .flatMap(response => response.data)
        .sort((a, b) => timestamp(b.created_at) - timestamp(a.created_at));
    },
  });
}

/**
 * The order detail of `/orders/info` (address, delivery, payment, prices), `null` when unknown. The
 * endpoint looks the order up by number and e-mail – the account's e-mail. A refused lookup (4xx)
 * is not retried.
 */
export function useOrderInfo(identifier: string) {
  const locale = useLocaleStore(s => s.currentLocale);
  const email = useCustomerStore(s => s.me?.customer.email ?? '');

  return useQuery({
    queryKey: [CacheKey.ORDER_INFO, ...privateQueryContext(locale, locale, identifier, email)],
    ...privateQueryOptions,
    enabled: !!email,
    queryFn: () => fetchOrderInfo({ locale, identifier, email }).then(response => response.data),
    retry: (failures, error) =>
      !(error instanceof FetchError && error.status < 500) && failures < 3,
  });
}

export function usePrice() {
  const { currency, currency_settings: currencySettings } = useGlobalStore(s => s.generalLayout);
  return (value: number) => formatPrice(value, currency, currencySettings);
}

/**
 * The number the customer knows the order by – also the key of the detail URL and `/orders/info`.
 */
export function orderNumber(order: Pick<CustomerOrder, 'id' | 'identifier' | 'order_identifier'>) {
  return order.identifier || order.order_identifier || String(order.id);
}

export function orderTotal(order: Pick<CustomerOrder, 'price' | 'price_with_vat'>) {
  return order.price_with_vat ?? order.price;
}

/** „8. 10. 2026“ of an API timestamp, empty when it cannot be read. */
export function orderDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('sk', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

/** „14:32“ of an API timestamp, empty when it cannot be read. */
export function orderTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString('sk', { hour: '2-digit', minute: '2-digit' });
}

/** Lower case without diacritics – the search matches „hmozdinka“ as well as „Hmoždinka“. */
export function searchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}
